/**
 * useAsyncAction — Generic hook for managing loading/error/data state
 * around async operations. Replaces the manual useState triple pattern
 * found across multiple screens.
 */

import { useState, useCallback } from 'react';

export function useAsyncAction<T, A extends unknown[]>(
  fn: (...args: A) => Promise<T>
): {
  execute: (...args: A) => Promise<T>;
  data: T | null;
  loading: boolean;
  error: string | null;
  reset: () => void;
} {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const execute = useCallback(
    async (...args: A): Promise<T> => {
      setLoading(true);
      setError(null);
      try {
        const result = await fn(...args);
        setData(result);
        return result;
      } catch (e) {
        const message =
          e instanceof Error ? e.message : 'An unexpected error occurred';
        setError(message);
        throw e;
      } finally {
        setLoading(false);
      }
    },
    [fn]
  );

  const reset = useCallback(() => {
    setData(null);
    setError(null);
    setLoading(false);
  }, []);

  return { execute, data, loading, error, reset };
}
